import React from "react"
import Card from "@material-ui/core/Card"
import CardContent from "@material-ui/core/CardContent"
import Dialog from "@material-ui/core/Dialog"
import DialogContent from "@material-ui/core/DialogContent"
import Typography from "@material-ui/core/Typography"
import TaskDetails from "../../../tasks/components/TaskDetails"
import TaskCardTitle from "../../../tasks/components/TaskCardTitle"
import TodoCardTitle from "../../../todos/components/TodoCardTitle"
import TodoDetails from "../../../todos/components/TodoDetails"
import IconButton from "@material-ui/core/IconButton"
import FullPath from "../../../components/FullPath"
import { Draggable } from "react-beautiful-dnd"
import { CircularProgress, makeStyles, Theme } from "@material-ui/core"
import { X } from "react-feather"
import { COLOR_BLACK } from "../../../constants"
import { CardHeader } from "./CardHeader"
import { BoardViewModelV2 } from "../../api/v2/board-view-model"
import { useGetTaskByIdQuery } from "../../../tasks/api"
import { useAppContext } from "../../../app-context/use-app-context"
import { getTodoQueryFromContext } from "../../../context-options/utils"
import { AppMainContextType, AppSubContextType } from "../../../types/app-context"
import { todoApi, useGetTodosQuery } from "../../../todos/api"
import { useLazyGetBoardV2Query } from "../../api/v2"
import { CardFooter } from "./CardFooter"
import { CardColorIndicator } from "./CardColorIndicator"
import { useAppDispatch } from "../../../store"

type BoardCardViewModel = BoardViewModelV2["columnsById"][string]["cards"][number]
type BoardCardProps = { board: BoardViewModelV2; card: BoardCardViewModel }

const getTodosQuery = (board: BoardViewModelV2, context: AppMainContextType | AppSubContextType | null) => {
  if (board.isProjectBoard) return { project: board.id }
  if (board.isTaskBoard) return { task: board.id }
  return context ? getTodoQueryFromContext(context) : {}
}

const isTempCardId = (id: string) => id.startsWith("temp-")

const BoardCard = ({ board, card }: BoardCardProps) => {
  const [refetchBoard] = useLazyGetBoardV2Query()
  const { appContext } = useAppContext()
  const classes = useStyles()
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const openDialog = () => setDialogOpen(true)
  const dispatch = useAppDispatch()
  const closeDialog = React.useCallback(() => {
    refetchBoard(board)
    setDialogOpen(false)
    dispatch(
      todoApi.util.invalidateTags([
        "Todo",
        { type: "Todo", id: card.isTodoCard ? card.id : "" },
        { type: "Board", id: board.id },
      ])
    )
  }, [refetchBoard, board, card, dispatch])

  // Fetch task to render the dialog
  const skipFetchingTask = card.isTodoCard || !dialogOpen || isTempCardId(card.id)
  const taskQueryResult = useGetTaskByIdQuery(card.id, {
    skip: skipFetchingTask,
  })

  // Fetch todo todo details to render the dialog
  const context = appContext.subContext || appContext.mainContext
  const todosQuery = getTodosQuery(board, context)
  const skipFetchingTodos = card.isTaskCard || !dialogOpen || context?.type === "supplier" || isTempCardId(card.id)
  const todoQueryResult = useGetTodosQuery(todosQuery, { skip: skipFetchingTodos })

  const { currentData: taskDetails, isLoading: isLoadingTask } = taskQueryResult
  const { data: todos, isLoading: isLoadingTodos } = todoQueryResult
  const showModalSpinner = (card.isTaskCard && isLoadingTask) || (card.isTodoCard && isLoadingTodos)
  const todoDetails = todos?.find((t) => t.id === card.id)
  const todoNotFound = card.isTodoCard && !isLoadingTodos && !todoDetails
  // Find which column this card currently lives in
  const columnPosition = Object.values(board.columnsById).find((col) =>
    col.cards.some((c) => c.id === card.id)
  )?.position
  // If the card is todo-card and modal is open but we don't have the todo details,
  // it means the todo was deleted. In that case, we should close the modal.
  React.useEffect(() => {
    if (todoNotFound && dialogOpen) {
      closeDialog()
    }
  }, [todoNotFound, dialogOpen, closeDialog])

  return (
    <>
      <Draggable draggableId={card.id} index={card.position}>
        {(provided) => (
          <Card
            className={classes.cardRoot}
            {...provided.dragHandleProps}
            {...provided.draggableProps}
            ref={provided.innerRef}
            onClick={openDialog}
            variant="outlined"
          >
            <CardColorIndicator color={card.color} />
            <div style={{ paddingLeft: 8 }}>
              <CardHeader board={board} card={card} />
              <CardContent className={classes.cardContent}>
                <Typography variant="body2" component="h2" gutterBottom>
                  {card.title}
                </Typography>
              </CardContent>
              <CardFooter card={card} />
            </div>
          </Card>
        )}
      </Draggable>
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        PaperProps={{
          style: {
            width: "100%",
            maxWidth: 1040,
            padding: "16px 0px",
          },
        }}
        disablePortal
      >
        <div className={classes.modalHeader}>
          <div style={{ flex: 1 }}>
            {taskDetails && (
              <FullPath
                projectLink={taskDetails.projectLink}
                taskLink={taskDetails.taskLink}
                ancestorTaskLinks={taskDetails.ancestorTaskLinks}
                projectOrTaskNumber={taskDetails.shownTaskNumber}
                onRedirect={closeDialog}
              />
            )}
            {todoDetails && (
              <FullPath
                projectLink={todoDetails.projectLink}
                taskLink={todoDetails.taskLink}
                ancestorTaskLinks={todoDetails.ancestorTaskLinks}
                projectOrTaskNumber={todoDetails.task?.taskNumber || todoDetails.project?.projectNumber || ""}
                onRedirect={closeDialog}
                showTaskLink={true}
              />
            )}
          </div>
          <IconButton className={classes.closeIcon} onClick={closeDialog}>
            <X size={18} color={COLOR_BLACK} />
          </IconButton>
        </div>
        <div className={classes.modalTitle}>
          {taskDetails && <TaskCardTitle task={taskDetails} />}
          {todoDetails && <TodoCardTitle todo={todoDetails} />}
        </div>
        <DialogContent style={{ paddingBottom: "16px " }}>
          {showModalSpinner ? <CircularProgress style={{ display: "block", margin: "48px auto" }} /> : null}
          {taskDetails && (
            <TaskDetails
              taskId={card.id}
              boardId={board.id}
              board={board}
              cardId={card.id}
              columnPosition={columnPosition}
            />
          )}
          {todoDetails && (
            <TodoDetails todo={todoDetails} board={board} cardId={card.id} columnPosition={columnPosition} />
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  cardRoot: {
    marginBottom: theme.spacing(),
    position: "relative",
    border: `1px solid ${theme.palette.grey[300]}`,
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 0, 0, 2.5),
  },
  modalTitle: {
    paddingLeft: theme.spacing(1.5),
  },
  closeIcon: {
    marginRight: 16,
  },
  cardContent: {
    padding: theme.spacing(0.5, 1, 0, 1),
  },
}))

export default BoardCard
